import { useEffect, useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { DollarSign, Plus, X, AlertTriangle, CheckCircle2, Clock, RefreshCw, FileSignature, Pencil, Trash2, Undo2, Check } from "lucide-react";
import { MoneyInput } from "../../components/MoneyInput";
import {
  buscarContratoPorProjeto,
  criarContrato,
  atualizarContrato,
  registrarPagamento,
  cancelarMensalidade,
  atualizarParcela,
  deletarParcela,
  estornarPagamento,
  recalcularParcelasPendentes,
  Contrato,
  Parcela,
} from "../../services/financeiro";
import { useAuth } from "../../hooks/useAuth";
import { useConfirm } from "../../components/ConfirmProvider";
import { confirmarDuasVezes } from "../../utils/confirmarDuasVezes";

const MODALIDADE_LABELS: Record<string, string> = {
  unico: "Pagamento único",
  parcelado: "Parcelado",
  mensalidade: "Mensalidade",
  setup_mensalidade: "Setup + Mensalidade",
};

const STATUS_PARCELA: Record<string, string> = {
  pendente: "bg-amber-100 text-amber-700",
  parcial: "bg-blue-100 text-blue-700",
  pago: "bg-emerald-100 text-emerald-700",
  atrasado: "bg-red-100 text-red-700",
  cancelada: "bg-slate-200 text-slate-500",
};

interface Props {
  projeto: {
    id: string;
    nome: string;
    valor?: number | null;
    valorRecorrente?: number | null;
    inicioSuporte?: string | null;
    fimSuporte?: string | null;
    prazoEntrega?: string;
  };
}

export function ProjetoFinanceiroSection({ projeto }: Props) {
  const { can } = useAuth();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showCriar, setShowCriar] = useState(false);
  const [pay, setPay] = useState<Parcela | null>(null);

  const { data: contrato, isLoading } = useQuery({
    queryKey: ["contrato-projeto", projeto.id],
    queryFn: () => buscarContratoPorProjeto(projeto.id),
    enabled: can(["gestor", "admin"]),
  });

  // Auto-abre o modal se veio com ?criarContrato=1 e ainda não tem contrato
  useEffect(() => {
    if (searchParams.get("criarContrato") === "1" && !isLoading && !contrato) {
      setShowCriar(true);
      // Limpa o query param pra não reabrir em refresh
      searchParams.delete("criarContrato");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contrato, isLoading]);

  if (!can(["gestor", "admin"])) return null;

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <p className="text-sm text-slate-400">Carregando financeiro…</p>
      </div>
    );
  }

  if (!contrato) {
    const temUnico = (projeto.valor || 0) > 0;
    const temRecorrente = (projeto.valorRecorrente || 0) > 0;
    const temAlgum = temUnico || temRecorrente;
    const sugestao =
      temUnico && temRecorrente ? "setup_mensalidade" :
      temRecorrente ? "mensalidade" :
      temUnico ? "unico" :
      null;

    return (
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="text-sm font-medium text-slate-700 flex items-center gap-2 mb-3">
          <DollarSign size={16} className="text-emerald-600" /> Financeiro do projeto
        </h3>

        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            {temAlgum
              ? "Nenhum contrato financeiro criado ainda. Posso gerar o cronograma de cobrança a partir dos valores do projeto."
              : "Nenhum contrato financeiro criado ainda. Defina os valores no modal ao clicar abaixo."}
          </p>

          {sugestao && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm">
              <p className="text-xs font-semibold text-emerald-800 uppercase mb-1">Sugestão automática</p>
              <p className="text-emerald-900">
                <strong>Modalidade:</strong> {MODALIDADE_LABELS[sugestao]}
              </p>
              {temUnico && <p className="text-emerald-900 text-xs mt-0.5">Setup: R$ {Number(projeto.valor).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</p>}
              {temRecorrente && <p className="text-emerald-900 text-xs">Mensalidade: R$ {Number(projeto.valorRecorrente).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}/mês</p>}
            </div>
          )}

          <button
            onClick={() => setShowCriar(true)}
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
          >
            <Plus size={14} /> Criar contrato financeiro
          </button>
        </div>

        {showCriar && (
          <CriarContratoModal
            projeto={projeto}
            onClose={() => setShowCriar(false)}
            onSuccess={() => {
              setShowCriar(false);
              qc.invalidateQueries({ queryKey: ["contrato-projeto", projeto.id] });
              qc.invalidateQueries({ queryKey: ["contratos"] });
              qc.invalidateQueries({ queryKey: ["financeiro-metricas"] });
            }}
          />
        )}
      </div>
    );
  }

  return (
    <ContratoView
      contrato={contrato}
      projetoId={projeto.id}
      onPagar={(p) => setPay(p)}
      onAtualizar={() => {
        qc.invalidateQueries({ queryKey: ["contrato-projeto", projeto.id] });
        qc.invalidateQueries({ queryKey: ["contratos"] });
        qc.invalidateQueries({ queryKey: ["mensalidades"] });
        qc.invalidateQueries({ queryKey: ["financeiro-metricas"] });
      }}
    >
      {pay && (
        <PagamentoModal
          parcela={pay}
          onClose={() => setPay(null)}
          onSuccess={() => {
            setPay(null);
            qc.invalidateQueries({ queryKey: ["contrato-projeto", projeto.id] });
            qc.invalidateQueries({ queryKey: ["contratos"] });
            qc.invalidateQueries({ queryKey: ["financeiro-metricas"] });
          }}
        />
      )}
    </ContratoView>
  );
}

function ContratoView({ contrato, projetoId, onPagar, onAtualizar, children }: {
  contrato: Contrato;
  projetoId: string;
  onPagar: (p: Parcela) => void;
  onAtualizar: () => void;
  children?: React.ReactNode;
}) {
  const qc = useQueryClient();
  const [confirmCancelMensalidade, setConfirmCancelMensalidade] = useState(false);
  const [showEditar, setShowEditar] = useState(false);
  const [showTodasParcelas, setShowTodasParcelas] = useState(false);

  const cancelMensM = useMutation({
    mutationFn: (motivo?: string) => cancelarMensalidade(contrato.id, motivo),
    onSuccess: () => {
      setConfirmCancelMensalidade(false);
      onAtualizar();
    },
  });

  const valorPago = contrato.parcelas.reduce((s, p) => s + (Number(p.valorPago) || 0), 0);
  const totalParcelas = contrato.parcelas.length;
  const parcelasPagas = contrato.parcelas.filter((p) => p.status === "pago").length;
  const proximas = contrato.parcelas
    .filter((p) => p.status === "pendente" || p.status === "atrasado" || p.status === "parcial")
    .sort((a, b) => new Date(a.vencimento).getTime() - new Date(b.vencimento).getTime())
    .slice(0, 6);

  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-sm font-medium text-slate-700 flex items-center gap-2">
            <DollarSign size={16} className="text-emerald-600" /> Financeiro do projeto
          </h3>
          <p className="text-xs text-slate-500 mt-1">
            {MODALIDADE_LABELS[contrato.modalidade]}
            {contrato.contratoAssinadoEm && (
              <span className="ml-2 inline-flex items-center gap-1 text-emerald-700">
                · <FileSignature size={10} /> assinado em {new Date(contrato.contratoAssinadoEm).toLocaleDateString("pt-BR")}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowEditar(true)}
            className="text-xs px-2.5 py-1 rounded border border-slate-300 hover:bg-slate-100 text-slate-700"
            title="Editar contrato"
          >
            Editar
          </button>
          <span className={`text-[10px] uppercase font-bold px-2 py-1 rounded ${
            contrato.status === "pago" ? "bg-emerald-100 text-emerald-700" :
            contrato.status === "atrasado" ? "bg-red-100 text-red-700" :
            contrato.status === "parcial" ? "bg-blue-100 text-blue-700" :
            "bg-amber-100 text-amber-700"
          }`}>
            {contrato.status}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        <Stat label="Valor total" valor={`R$ ${Number(contrato.valorTotal).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`} />
        <Stat label="Recebido" valor={`R$ ${valorPago.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`} cor="text-emerald-700" />
        <Stat label="Saldo" valor={`R$ ${(Number(contrato.valorTotal) - valorPago).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`} cor="text-slate-800" />
        <Stat label="Parcelas pagas" valor={`${parcelasPagas} / ${totalParcelas}`} />
      </div>

      {contrato.valorSetup != null && contrato.valorSetup > 0 && (
        <div className="text-xs text-slate-600">
          Setup: <strong>R$ {Number(contrato.valorSetup).toFixed(2)}</strong>
          {contrato.mensalidade && (
            <span className="ml-3">
              Mensalidade: <strong>R$ {Number(contrato.mensalidade.valorMensal).toFixed(2)}/mês</strong>
              {contrato.mensalidade.ativa === false && <span className="ml-1 text-red-600">(cancelada)</span>}
            </span>
          )}
        </div>
      )}

      {/* Próximos vencimentos / parcelas pendentes */}
      {proximas.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Próximos vencimentos</p>
          <div className="space-y-1.5">
            {proximas.map((p) => {
              const venc = new Date(p.vencimento); venc.setHours(0, 0, 0, 0);
              const atrasada = venc < hoje;
              return (
                <div
                  key={p.id}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${atrasada ? "bg-red-50 border-red-200" : "bg-slate-50 border-slate-200"}`}
                >
                  <span className="text-xs font-medium text-slate-700">#{p.numero}</span>
                  <span className="text-xs text-slate-500">{venc.toLocaleDateString("pt-BR")}</span>
                  {atrasada && <span className="text-[10px] text-red-600 font-bold uppercase">atrasada</span>}
                  <span className="ml-auto text-sm font-semibold text-slate-800">R$ {Number(p.valor).toFixed(2)}</span>
                  <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${STATUS_PARCELA[p.status]}`}>
                    {p.status}
                  </span>
                  <button
                    onClick={() => onPagar(p)}
                    className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-2.5 py-1 rounded font-medium"
                  >
                    Pagar
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Mensalidade ativa — botão cancelar */}
      {contrato.mensalidade && contrato.mensalidade.ativa && (
        <div className="flex justify-end pt-2 border-t border-slate-100">
          <button
            onClick={() => setConfirmCancelMensalidade(true)}
            className="text-xs text-red-600 hover:text-red-700 inline-flex items-center gap-1"
          >
            <RefreshCw size={11} /> Cancelar assinatura mensal
          </button>
        </div>
      )}

      {confirmCancelMensalidade && (
        <CancelarMensalidadeModal
          onClose={() => setConfirmCancelMensalidade(false)}
          onConfirm={(motivo) => cancelMensM.mutate(motivo)}
          loading={cancelMensM.isPending}
          erro={(cancelMensM.error as any)?.response?.data?.error?.message}
        />
      )}

      {showEditar && (
        <EditarContratoModal
          contrato={contrato}
          onClose={() => setShowEditar(false)}
          onSuccess={() => {
            setShowEditar(false);
            qc.invalidateQueries({ queryKey: ["contrato-projeto", projetoId] });
            qc.invalidateQueries({ queryKey: ["contratos"] });
            qc.invalidateQueries({ queryKey: ["mensalidades"] });
          }}
        />
      )}

      {children}

      {/* Link pra ver detalhes completos */}
      <div className="text-[11px] text-slate-400 pt-2 border-t border-slate-100">
        ID do contrato: <span className="font-mono">{contrato.id.slice(0, 8)}</span> · Total {totalParcelas} parcela(s) ·
        {" "}
        <button
          onClick={() => setShowTodasParcelas(true)}
          className="text-blue-600 hover:underline"
        >
          ver todas as parcelas
        </button>
      </div>

      {showTodasParcelas && (
        <TodasParcelasModal
          contrato={contrato}
          onClose={() => setShowTodasParcelas(false)}
          onPagar={(p) => { setShowTodasParcelas(false); onPagar(p); }}
        />
      )}

      <input type="hidden" value={projetoId} />
    </div>
  );
}

export function TodasParcelasModal({ contrato, onClose, onPagar }: {
  contrato: Contrato;
  onClose: () => void;
  onPagar: (p: Parcela) => void;
}) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [editandoId, setEditandoId] = useState<string | null>(null);
  const [editValor, setEditValor] = useState<number>(0);
  const [editVenc, setEditVenc] = useState<string>("");

  const parcelas = [...contrato.parcelas].sort(
    (a, b) => new Date(a.vencimento).getTime() - new Date(b.vencimento).getTime(),
  );
  const totalPago = parcelas.reduce((s, p) => s + (Number(p.valorPago) || 0), 0);
  const totalContrato = parcelas.reduce((s, p) => s + Number(p.valor), 0);
  const pagas = parcelas.filter((p) => p.status === "pago").length;

  const invalidar = () => {
    qc.invalidateQueries({ queryKey: ["contrato-projeto", contrato.projetoId] });
    qc.invalidateQueries({ queryKey: ["contratos"] });
    qc.invalidateQueries({ queryKey: ["mensalidades"] });
    qc.invalidateQueries({ queryKey: ["parcelas-vencidas"] });
    qc.invalidateQueries({ queryKey: ["parcelas-geral"] });
  };

  const updateM = useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: { valor?: number; vencimento?: string } }) =>
      atualizarParcela(id, dados),
    onSuccess: () => {
      setEditandoId(null);
      invalidar();
    },
  });
  const deleteM = useMutation({
    mutationFn: deletarParcela,
    onSuccess: invalidar,
  });
  const estornarM = useMutation({
    mutationFn: estornarPagamento,
    onSuccess: invalidar,
  });

  function abrirEdicao(p: Parcela) {
    setEditandoId(p.id);
    setEditValor(Number(p.valor));
    // Input type=date precisa formato YYYY-MM-DD em local time. Usa só a data sem fuso pra evitar offset.
    const d = new Date(p.vencimento);
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    setEditVenc(iso);
  }

  function salvarEdicao(p: Parcela) {
    const dados: { valor?: number; vencimento?: string } = {};
    if (editValor !== Number(p.valor)) dados.valor = editValor;
    const original = (() => {
      const d = new Date(p.vencimento);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    })();
    if (editVenc !== original) dados.vencimento = editVenc;
    if (Object.keys(dados).length === 0) {
      setEditandoId(null);
      return;
    }
    updateM.mutate({ id: p.id, dados });
  }

  async function aoApagar(p: Parcela) {
    const ok = await confirm({
      title: `Apagar parcela #${p.numero}?`,
      message: "A parcela será removida do contrato em definitivo. Pagamentos registrados nela são perdidos. Use 'Estornar' se quiser desfazer só o pagamento.",
      danger: true,
      confirmText: "Apagar",
    });
    if (ok) deleteM.mutate(p.id);
  }

  async function aoEstornar(p: Parcela) {
    const ok = await confirm({
      title: `Estornar pagamento da parcela #${p.numero}?`,
      message: "O pagamento registrado será desfeito. A parcela volta a 'pendente' (ou 'atrasado' se já passou do vencimento).",
      danger: true,
      confirmText: "Estornar",
    });
    if (ok) estornarM.mutate(p.id);
  }

  const FORMA_LABEL: Record<string, string> = {
    pix: "Pix",
    boleto: "Boleto",
    cartao: "Cartão",
    transferencia: "Transferência",
    dinheiro: "Dinheiro",
    outro: "Outro",
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-slate-900 rounded-xl w-full max-w-3xl max-h-[85vh] flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 p-5 border-b border-slate-200 dark:border-slate-700">
          <div>
            <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100">
              Histórico de pagamento
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {contrato.projeto?.nome || "Contrato"} · {parcelas.length} parcela(s)
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200" aria-label="Fechar">
            <X size={18} />
          </button>
        </div>

        {/* Resumo */}
        <div className="grid grid-cols-3 gap-3 px-5 py-3 border-b border-slate-100 dark:border-slate-800 text-xs">
          <div>
            <p className="text-slate-500 uppercase font-medium">Recebido</p>
            <p className="font-semibold text-emerald-700 tabular-nums">R$ {totalPago.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</p>
          </div>
          <div>
            <p className="text-slate-500 uppercase font-medium">Total contrato</p>
            <p className="font-semibold text-slate-800 dark:text-slate-100 tabular-nums">R$ {totalContrato.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</p>
          </div>
          <div>
            <p className="text-slate-500 uppercase font-medium">Parcelas pagas</p>
            <p className="font-semibold text-slate-800 dark:text-slate-100 tabular-nums">{pagas} / {parcelas.length}</p>
          </div>
        </div>

        {/* Tabela */}
        <div className="flex-1 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 dark:bg-slate-800 sticky top-0">
              <tr className="text-left text-slate-500 uppercase tracking-wide">
                <th className="px-4 py-2 font-medium">#</th>
                <th className="px-2 py-2 font-medium">Vencimento</th>
                <th className="px-2 py-2 font-medium text-right">Valor</th>
                <th className="px-2 py-2 font-medium text-right">Pago</th>
                <th className="px-2 py-2 font-medium">Pagamento</th>
                <th className="px-2 py-2 font-medium">Forma</th>
                <th className="px-2 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium"></th>
              </tr>
            </thead>
            <tbody className="text-slate-700 dark:text-slate-200">
              {parcelas.map((p) => {
                const podePagar = p.status === "pendente" || p.status === "atrasado" || p.status === "parcial";
                const ehPago = p.status === "pago" || p.status === "parcial";
                const editando = editandoId === p.id;
                return (
                  <tr key={p.id} className="border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                    <td className="px-4 py-2 font-medium">{p.numero}</td>
                    <td className="px-2 py-2">
                      {editando ? (
                        <input
                          type="date"
                          value={editVenc}
                          onChange={(e) => setEditVenc(e.target.value)}
                          className="px-1 py-0.5 rounded border border-slate-300 dark:border-slate-600 dark:bg-slate-800 text-xs outline-none focus:border-blue-500"
                        />
                      ) : (
                        new Date(p.vencimento).toLocaleDateString("pt-BR")
                      )}
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums">
                      {editando ? (
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={editValor}
                          onChange={(e) => setEditValor(Number(e.target.value) || 0)}
                          className="w-24 px-1 py-0.5 rounded border border-slate-300 dark:border-slate-600 dark:bg-slate-800 text-xs text-right outline-none focus:border-blue-500"
                        />
                      ) : (
                        `R$ ${Number(p.valor).toFixed(2)}`
                      )}
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums">
                      {p.valorPago != null ? `R$ ${Number(p.valorPago).toFixed(2)}` : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-2 py-2">
                      {p.dataPagamento ? new Date(p.dataPagamento).toLocaleDateString("pt-BR") : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-2 py-2">
                      {p.formaPagamento ? FORMA_LABEL[p.formaPagamento] || p.formaPagamento : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-2 py-2">
                      <span className={`inline-block text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${STATUS_PARCELA[p.status] || ""}`}>
                        {p.status}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <div className="inline-flex items-center gap-1">
                        {editando ? (
                          <>
                            <button
                              onClick={() => salvarEdicao(p)}
                              disabled={updateM.isPending}
                              className="p-1 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 rounded disabled:opacity-50"
                              title="Salvar"
                            >
                              <Check size={14} />
                            </button>
                            <button
                              onClick={() => setEditandoId(null)}
                              className="p-1 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 rounded"
                              title="Cancelar"
                            >
                              <X size={14} />
                            </button>
                          </>
                        ) : (
                          <>
                            {podePagar && (
                              <button
                                onClick={() => onPagar(p)}
                                className="text-[11px] bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded font-medium"
                              >
                                Pagar
                              </button>
                            )}
                            {ehPago && (
                              <button
                                onClick={() => aoEstornar(p)}
                                disabled={estornarM.isPending}
                                className="p-1 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/30 rounded disabled:opacity-50"
                                title="Estornar pagamento"
                              >
                                <Undo2 size={13} />
                              </button>
                            )}
                            <button
                              onClick={() => abrirEdicao(p)}
                              className="p-1 text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded"
                              title="Editar valor/vencimento"
                            >
                              <Pencil size={13} />
                            </button>
                            <button
                              onClick={() => aoApagar(p)}
                              disabled={deleteM.isPending}
                              className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/30 rounded disabled:opacity-50"
                              title="Apagar parcela"
                            >
                              <Trash2 size={13} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 dark:border-slate-700 px-5 py-3 flex justify-end">
          <button onClick={onClose} className="text-sm px-3 py-1.5 rounded bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-slate-100">
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, valor, cor }: { label: string; valor: string; cor?: string }) {
  // Reduz tamanho conforme o comprimento pra não quebrar layout
  const tamClasse = valor.length <= 10
    ? "text-base"
    : valor.length <= 13
      ? "text-sm"
      : valor.length <= 16
        ? "text-xs"
        : "text-[11px]";
  return (
    <div className="min-w-0">
      <p className="text-[11px] text-slate-500 uppercase font-medium">{label}</p>
      <p
        className={`font-semibold tabular-nums whitespace-nowrap overflow-hidden text-ellipsis ${tamClasse} ${cor || "text-slate-800"}`}
        title={valor}
      >
        {valor}
      </p>
    </div>
  );
}

function CriarContratoModal({ projeto, onClose, onSuccess }: {
  projeto: Props["projeto"];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const valorInicial = projeto.valor || 0;
  const recorrenteInicial = projeto.valorRecorrente || 0;
  const modalidadeSugerida =
    valorInicial > 0 && recorrenteInicial > 0 ? "setup_mensalidade" :
    recorrenteInicial > 0 ? "mensalidade" :
    "unico";

  const [modalidade, setModalidade] = useState(modalidadeSugerida);
  const [valorSetup, setValorSetup] = useState<number>(valorInicial);
  const [valorMensal, setValorMensal] = useState<number>(recorrenteInicial);
  const [diaVencimento, setDiaVencimento] = useState<number>(10);
  // Carência da 1ª cobrança (default 30 dias). Evita primeira parcela parecer
  // "atrasada" quando o cliente assinou logo antes do dia X. 0 = cobra no
  // próximo dia X após inicioCobranca.
  const [carenciaDias, setCarenciaDias] = useState<number>(30);
  // "Cliente não definiu o dia de pagamento" — quando true, não envia dia/inicio/fim;
  // o gestor pode preencher depois ao editar o contrato.
  const [semDiaDefinido, setSemDiaDefinido] = useState(false);
  const [primeiraParcela, setPrimeiraParcela] = useState(new Date().toISOString().split("T")[0]);
  const [inicioCobranca, setInicioCobranca] = useState(() => {
    if (projeto.inicioSuporte) return projeto.inicioSuporte.split("T")[0];
    if (projeto.prazoEntrega) return projeto.prazoEntrega.split("T")[0];
    return new Date().toISOString().split("T")[0];
  });
  const [fimCobranca, setFimCobranca] = useState(projeto.fimSuporte ? projeto.fimSuporte.split("T")[0] : "");
  const [numParcelas, setNumParcelas] = useState(3);
  const [contratoAssinadoEm, setContratoAssinadoEm] = useState("");
  const [observacoes, setObservacoes] = useState("");

  // Setup parcial — cliente pagou só uma parte
  const [setupParcial, setSetupParcial] = useState(false);
  const [valorSetupPago, setValorSetupPago] = useState<number>(0);
  const [restanteTipo, setRestanteTipo] = useState<"data" | "finalProjeto">("data");
  const [restanteData, setRestanteData] = useState(new Date().toISOString().split("T")[0]);
  const valorSetupRestante = Math.max(0, valorSetup - valorSetupPago);

  const temSetup = modalidade === "setup_mensalidade" || modalidade === "unico";

  const valorTotal = useMemo(() => {
    if (modalidade === "unico") return valorSetup;
    if (modalidade === "parcelado") return valorSetup;
    if (modalidade === "mensalidade") return valorMensal * 12;
    if (modalidade === "setup_mensalidade") return valorSetup + valorMensal * 12;
    return 0;
  }, [modalidade, valorSetup, valorMensal]);

  const criarM = useMutation({
    mutationFn: () => {
      const payload: any = {
        projetoId: projeto.id,
        modalidade,
        valorTotal,
        observacoes: observacoes || undefined,
      };
      if (contratoAssinadoEm) payload.contratoAssinadoEm = contratoAssinadoEm;
      if (modalidade === "mensalidade") {
        payload.valorMensal = valorMensal;
        // Só envia dia/inicio/fim se o cliente já definiu. Caso contrário, gestor edita depois.
        if (!semDiaDefinido) {
          payload.diaVencimento = diaVencimento;
          payload.inicioCobranca = inicioCobranca;
          if (fimCobranca) payload.fimCobranca = fimCobranca;
          payload.carenciaDias = carenciaDias;
        }
      } else if (modalidade === "setup_mensalidade") {
        payload.valorSetup = valorSetup;
        payload.valorMensal = valorMensal;
        if (!semDiaDefinido) {
          payload.diaVencimento = diaVencimento;
          payload.inicioCobranca = inicioCobranca;
          if (fimCobranca) payload.fimCobranca = fimCobranca;
          payload.carenciaDias = carenciaDias;
        }
        // Setup tem seu próprio vencimento (campo "Vencimento Setup"); deixamos o
        // backend usar o default (data atual) quando não enviado.
      } else if (modalidade === "parcelado") {
        payload.numParcelas = numParcelas;
        payload.primeiraParcelaVencimento = primeiraParcela;
      } else {
        payload.primeiraParcelaVencimento = primeiraParcela;
      }

      // Setup parcial — cliente já pagou parte
      if (temSetup && setupParcial && valorSetupPago > 0 && valorSetupRestante > 0) {
        payload.valorSetupPago = valorSetupPago;
        if (restanteTipo === "finalProjeto" && projeto.prazoEntrega) {
          payload.valorRestanteVencimento = projeto.prazoEntrega.split("T")[0];
        } else {
          payload.valorRestanteVencimento = restanteData;
        }
      }
      return criarContrato(payload);
    },
    onSuccess,
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-semibold text-slate-800">Criar contrato financeiro</h3>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-3">
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-900">
            Projeto: <strong>{projeto.nome}</strong>
          </div>

          <div>
            <label className="text-xs text-slate-600">Modalidade</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1">
              {(["setup_mensalidade", "mensalidade", "parcelado", "unico"] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setModalidade(m)}
                  className={`px-2 py-2 rounded-lg border text-xs font-medium ${modalidade === m ? "bg-blue-50 border-blue-400 text-blue-700" : "bg-white border-slate-300 text-slate-600"}`}
                >
                  {MODALIDADE_LABELS[m]}
                </button>
              ))}
            </div>
          </div>

          {/* Bloco do SETUP (valor único) — quando modalidade tem setup */}
          {temSetup && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-3">
              <p className="text-xs font-semibold text-blue-700 uppercase">
                {modalidade === "setup_mensalidade" ? "Setup (cobrança única)" : "Valor do projeto"}
              </p>
              <div>
                <label className="text-xs text-slate-600">Valor total</label>
                <MoneyInput value={valorSetup} onChange={setValorSetup} />
              </div>

              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={setupParcial}
                  onChange={(e) => setSetupParcial(e.target.checked)}
                  className="accent-blue-600"
                />
                Cliente pagou somente uma parte
              </label>

              {setupParcial && (
                <div className="space-y-3 pl-4 border-l-2 border-blue-300">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-slate-600">Valor pago até o momento</label>
                      <MoneyInput value={valorSetupPago} onChange={setValorSetupPago} />
                    </div>
                    <div>
                      <label className="text-xs text-slate-600">Valor restante a pagar</label>
                      <MoneyInput value={valorSetupRestante} onChange={() => {}} disabled />
                    </div>
                  </div>

                  <div>
                    <p className="text-xs text-slate-600 mb-1.5">Quando o restante será pago?</p>
                    <div className="space-y-2">
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="restanteTipo"
                          checked={restanteTipo === "data"}
                          onChange={() => setRestanteTipo("data")}
                          className="mt-1 accent-blue-600"
                        />
                        <div className="flex-1">
                          <span className="text-sm text-slate-700">Data de pagamento</span>
                          {restanteTipo === "data" && (
                            <input
                              type="date"
                              value={restanteData}
                              onChange={(e) => setRestanteData(e.target.value)}
                              className="block mt-1 px-3 py-1.5 rounded-lg border border-slate-300 outline-none text-sm"
                            />
                          )}
                        </div>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="restanteTipo"
                          checked={restanteTipo === "finalProjeto"}
                          onChange={() => setRestanteTipo("finalProjeto")}
                          className="accent-blue-600"
                        />
                        <span className="text-sm text-slate-700">
                          Na finalização do projeto
                          {projeto.prazoEntrega && (
                            <span className="text-xs text-slate-500 ml-1">
                              (prazo: {new Date(projeto.prazoEntrega).toLocaleDateString("pt-BR")})
                            </span>
                          )}
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Bloco da MENSALIDADE */}
          {(modalidade === "mensalidade" || modalidade === "setup_mensalidade") && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 space-y-3">
              <p className="text-xs font-semibold text-purple-700 uppercase">Mensalidade</p>

              <div>
                <label className="text-xs text-slate-600">Valor mensal</label>
                <MoneyInput value={valorMensal} onChange={setValorMensal} />
              </div>

              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={semDiaDefinido}
                  onChange={(e) => setSemDiaDefinido(e.target.checked)}
                  className="accent-purple-600"
                />
                Cliente não definiu o dia de pagamento
              </label>
              {semDiaDefinido && (
                <p className="text-[11px] text-purple-700 bg-purple-100 border border-purple-200 rounded px-2 py-1.5">
                  As datas de cobrança ficam em branco. Quando o cliente definir, edite o contrato pra preencher.
                </p>
              )}

              <div className={`grid grid-cols-1 sm:grid-cols-2 gap-3 ${semDiaDefinido ? "opacity-50 pointer-events-none" : ""}`}>
                <div>
                  <label className="text-xs text-slate-600">Dia do vencimento (1–31)</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={diaVencimento}
                    disabled={semDiaDefinido}
                    onChange={(e) => {
                      const n = parseInt(e.target.value.replace(/\D/g, ""), 10);
                      setDiaVencimento(Math.min(31, Math.max(1, isNaN(n) ? 1 : n)));
                    }}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm disabled:bg-slate-100"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-600">Início da cobrança</label>
                  <input type="date" value={inicioCobranca} disabled={semDiaDefinido} onChange={(e) => setInicioCobranca(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm disabled:bg-slate-100" />
                </div>
                <div>
                  <label className="text-xs text-slate-600">Fim da cobrança (opcional)</label>
                  <input type="date" value={fimCobranca} disabled={semDiaDefinido} onChange={(e) => setFimCobranca(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm disabled:bg-slate-100" />
                </div>
                <div>
                  <label className="text-xs text-slate-600">
                    Carência da 1ª cobrança (dias)
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={365}
                    value={carenciaDias}
                    disabled={semDiaDefinido}
                    onChange={(e) => setCarenciaDias(Math.max(0, Math.min(365, Number(e.target.value) || 0)))}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm disabled:bg-slate-100"
                  />
                  <p className="text-[10px] text-slate-500 mt-0.5">
                    A 1ª parcela será o próximo dia {diaVencimento} após {carenciaDias} dias do início da cobrança.
                    Use <strong>0</strong> pra cobrar no próximo dia {diaVencimento}.
                  </p>
                </div>
              </div>
            </div>
          )}

          {modalidade === "parcelado" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-600">Nº de parcelas</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={numParcelas}
                  onChange={(e) => {
                    const n = parseInt(e.target.value.replace(/\D/g, ""), 10);
                    setNumParcelas(Math.max(1, isNaN(n) ? 1 : n));
                  }}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-slate-600">1ª parcela vence em</label>
                <input type="date" value={primeiraParcela} onChange={(e) => setPrimeiraParcela(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm" />
              </div>
            </div>
          )}

          {modalidade === "unico" && (
            <div>
              <label className="text-xs text-slate-600">Vencimento</label>
              <input type="date" value={primeiraParcela} onChange={(e) => setPrimeiraParcela(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm" />
            </div>
          )}

          <div>
            <label className="text-xs text-slate-600">Contrato assinado em <span className="text-slate-400">(opcional)</span></label>
            <input type="date" value={contratoAssinadoEm} onChange={(e) => setContratoAssinadoEm(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm" />
          </div>

          <div>
            <label className="text-xs text-slate-600">Observações</label>
            <textarea value={observacoes} onChange={(e) => setObservacoes(e.target.value)} rows={2} className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm" />
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded p-2 text-xs text-slate-700 flex justify-between">
            <span>Valor total do contrato</span>
            <strong>R$ {valorTotal.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</strong>
          </div>

          {(criarM.error as any)?.response?.data?.error?.message && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
              {(criarM.error as any).response.data.error.message}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded text-sm bg-slate-100 hover:bg-slate-200">Cancelar</button>
          <button
            onClick={() => criarM.mutate()}
            disabled={criarM.isPending || valorTotal <= 0}
            className="px-4 py-1.5 rounded text-sm bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
          >
            {criarM.isPending ? "Criando…" : "Criar contrato"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function PagamentoModal({ parcela, onClose, onSuccess }: {
  parcela: Parcela;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [dataPagamento, setDataPagamento] = useState(new Date().toISOString().split("T")[0]);
  const [valorPago, setValorPago] = useState(Number(parcela.valor));
  const [formaPagamento, setFormaPagamento] = useState<string>("pix");

  const m = useMutation({
    mutationFn: () => registrarPagamento(parcela.id, { dataPagamento, valorPago, formaPagamento }),
    onSuccess,
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-sm shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-semibold text-slate-800">Registrar pagamento</h3>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-sm text-slate-600">
            Parcela #{parcela.numero} · vencimento {new Date(parcela.vencimento).toLocaleDateString("pt-BR")} · R$ {Number(parcela.valor).toFixed(2)}
          </p>
          <div>
            <label className="text-xs text-slate-600">Data do pagamento</label>
            <input type="date" value={dataPagamento} onChange={(e) => setDataPagamento(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm" />
          </div>
          <div>
            <label className="text-xs text-slate-600">Valor pago</label>
            <input type="number" step="0.01" min={0} value={valorPago} onChange={(e) => setValorPago(parseFloat(e.target.value) || 0)} className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm" />
          </div>
          <div>
            <label className="text-xs text-slate-600">Forma de pagamento</label>
            <select value={formaPagamento} onChange={(e) => setFormaPagamento(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm">
              <option value="pix">PIX</option>
              <option value="boleto">Boleto</option>
              <option value="cartao">Cartão</option>
              <option value="ted">TED</option>
              <option value="outro">Outro</option>
            </select>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded text-sm bg-slate-100 hover:bg-slate-200">Cancelar</button>
          <button
            onClick={() => m.mutate()}
            disabled={m.isPending || valorPago <= 0}
            className="px-4 py-1.5 rounded text-sm bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
          >
            {m.isPending ? "Registrando…" : "Registrar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CancelarMensalidadeModal({ onClose, onConfirm, loading, erro }: {
  onClose: () => void;
  onConfirm: (motivo?: string) => void;
  loading: boolean;
  erro?: string;
}) {
  const [motivo, setMotivo] = useState("");
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-semibold text-slate-800 flex items-center gap-2">
            <AlertTriangle size={16} className="text-red-500" /> Cancelar mensalidade
          </h3>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-sm text-slate-700">
            Cancelar a cobrança recorrente deste projeto. Parcelas pendentes futuras serão canceladas, parcelas pagas/vencidas mantêm-se como estão.
          </p>
          <div>
            <label className="text-xs text-slate-600">Motivo (opcional)</label>
            <textarea
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              rows={3}
              placeholder="Ex.: Cliente solicitou via e-mail em DD/MM"
              className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm"
            />
          </div>
          {erro && <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1">{erro}</div>}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded text-sm bg-slate-100 hover:bg-slate-200">Voltar</button>
          <button
            onClick={() => {
              const ok = confirmarDuasVezes({
                primeiraMensagem: "Cancelar a mensalidade?",
                segundaMensagem: "CONFIRMAR cancelamento? Cobranças futuras serão paradas.",
              });
              if (ok) onConfirm(motivo || undefined);
            }}
            disabled={loading}
            className="px-4 py-1.5 rounded text-sm bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
          >
            {loading ? "Cancelando…" : "Cancelar mensalidade"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function EditarContratoModal({ contrato, onClose, onSuccess }: {
  contrato: Contrato;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const m = contrato.mensalidade;
  const temMensalidade = contrato.modalidade === "mensalidade" || contrato.modalidade === "setup_mensalidade";

  const [valorTotal, setValorTotal] = useState<number>(Number(contrato.valorTotal) || 0);
  const [valorSetup, setValorSetup] = useState<number>(Number(contrato.valorSetup ?? 0));
  const [observacoes, setObservacoes] = useState(contrato.observacoes || "");
  const [contratoAssinadoEm, setContratoAssinadoEm] = useState(contrato.contratoAssinadoEm ? contrato.contratoAssinadoEm.split("T")[0] : "");
  const [valorMensal, setValorMensal] = useState<number>(Number(m?.valorMensal ?? 0));
  const [diaVencimento, setDiaVencimento] = useState<string>(m?.diaVencimento != null ? String(m.diaVencimento) : "");
  const [inicioCobranca, setInicioCobranca] = useState(m?.inicioCobranca ? m.inicioCobranca.split("T")[0] : "");
  const [fimCobranca, setFimCobranca] = useState(m?.fimCobranca ? m.fimCobranca.split("T")[0] : "");
  // Quando user mexe em dia/início/fim/valorMensal, oferece recalcular as
  // pendentes futuras pra alinhar. Default ligado se mexeu, desligado caso
  // contrário (UX: assume que ele quer ver mudança refletida).
  const [recalcular, setRecalcular] = useState(false);

  const updateM = useMutation({
    mutationFn: async () => {
      const payload: any = {};
      if (valorTotal !== Number(contrato.valorTotal)) payload.valorTotal = valorTotal;
      if (valorSetup !== Number(contrato.valorSetup ?? 0)) payload.valorSetup = valorSetup;
      if (observacoes !== (contrato.observacoes || "")) payload.observacoes = observacoes || null;
      const novoAssin = contratoAssinadoEm || null;
      const oldAssin = contrato.contratoAssinadoEm ? contrato.contratoAssinadoEm.split("T")[0] : null;
      if (novoAssin !== oldAssin) payload.contratoAssinadoEm = novoAssin;

      if (temMensalidade) {
        if (valorMensal !== Number(m?.valorMensal ?? 0)) payload.valorMensal = valorMensal;
        const dvNum = diaVencimento === "" ? null : parseInt(diaVencimento, 10);
        if (dvNum !== (m?.diaVencimento ?? null)) payload.diaVencimento = dvNum;
        const ic = inicioCobranca || null;
        const oldIc = m?.inicioCobranca ? m.inicioCobranca.split("T")[0] : null;
        if (ic !== oldIc) payload.inicioCobranca = ic;
        const fc = fimCobranca || null;
        const oldFc = m?.fimCobranca ? m.fimCobranca.split("T")[0] : null;
        if (fc !== oldFc) payload.fimCobranca = fc;
      }
      const r = await atualizarContrato(contrato.id, payload);
      if (recalcular && temMensalidade) {
        try {
          await recalcularParcelasPendentes(contrato.id);
        } catch (e) {
          // não bloqueia o save principal; o erro fica no console
          console.warn("recalcular parcelas falhou", e);
        }
      }
      return r;
    },
    onSuccess,
  });

  // Detecta se o usuário mexeu em algo que afeta parcelas futuras
  const mensalidadeMexida =
    temMensalidade && (
      valorMensal !== Number(m?.valorMensal ?? 0) ||
      (diaVencimento === "" ? null : parseInt(diaVencimento, 10)) !== (m?.diaVencimento ?? null) ||
      (inicioCobranca || null) !== (m?.inicioCobranca ? m.inicioCobranca.split("T")[0] : null) ||
      (fimCobranca || null) !== (m?.fimCobranca ? m.fimCobranca.split("T")[0] : null)
    );

  const erroMsg = (() => {
    const e: any = updateM.error;
    const apiErr = e?.response?.data?.error;
    if (!apiErr) return null;
    if (apiErr.issues && typeof apiErr.issues === "object") {
      const linhas: string[] = [];
      for (const [campo, msgs] of Object.entries(apiErr.issues)) {
        if (Array.isArray(msgs) && msgs.length) linhas.push(`${campo}: ${msgs.join(", ")}`);
      }
      if (linhas.length) return linhas.join(" | ");
    }
    return apiErr.message || null;
  })();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-semibold text-slate-800">Editar contrato financeiro</h3>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-3">
          <p className="text-xs text-slate-500">
            Modalidade: <strong>{MODALIDADE_LABELS[contrato.modalidade]}</strong> · Status: <strong>{contrato.status}</strong>
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-600">Valor total do contrato</label>
              <MoneyInput value={valorTotal} onChange={setValorTotal} />
            </div>
            {(contrato.modalidade === "setup_mensalidade") && (
              <div>
                <label className="text-xs text-slate-600">Valor do setup</label>
                <MoneyInput value={valorSetup} onChange={setValorSetup} />
              </div>
            )}
          </div>

          <div>
            <label className="text-xs text-slate-600">Contrato assinado em</label>
            <input type="date" value={contratoAssinadoEm} onChange={(e) => setContratoAssinadoEm(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm" />
          </div>

          {temMensalidade && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 space-y-3">
              <p className="text-xs font-semibold text-purple-700 uppercase">Mensalidade</p>
              <div>
                <label className="text-xs text-slate-600">Valor mensal</label>
                <MoneyInput value={valorMensal} onChange={setValorMensal} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs text-slate-600">Dia do vencimento (1–31)</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={diaVencimento}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/\D/g, "");
                      if (raw === "") { setDiaVencimento(""); return; }
                      const n = parseInt(raw, 10);
                      setDiaVencimento(String(Math.min(31, Math.max(1, n))));
                    }}
                    placeholder="—"
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-600">Início da cobrança</label>
                  <input type="date" value={inicioCobranca} onChange={(e) => setInicioCobranca(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm" />
                </div>
                <div>
                  <label className="text-xs text-slate-600">Fim da cobrança</label>
                  <input type="date" value={fimCobranca} onChange={(e) => setFimCobranca(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm" />
                </div>
              </div>
              <p className="text-[11px] text-purple-700">
                Deixar dia/início em branco mantém o contrato como "aguardando definição do cliente".
              </p>
            </div>
          )}

          <div>
            <label className="text-xs text-slate-600">Observações</label>
            <textarea value={observacoes} onChange={(e) => setObservacoes(e.target.value)} rows={2} className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm" />
          </div>

          {temMensalidade && mensalidadeMexida && (
            <label className="flex items-start gap-2 text-xs bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 rounded cursor-pointer">
              <input
                type="checkbox"
                checked={recalcular}
                onChange={(e) => setRecalcular(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                <strong>Recalcular parcelas pendentes futuras</strong> com o novo dia/início.
                <br />
                Apaga as não-pagas com vencimento ≥ hoje e gera a próxima conforme nova configuração.
                Parcelas pagas e parcelas no passado são preservadas.
              </span>
            </label>
          )}

          {erroMsg && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-xs">{erroMsg}</div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded text-sm bg-slate-100 hover:bg-slate-200">Cancelar</button>
          <button
            onClick={() => updateM.mutate()}
            disabled={updateM.isPending}
            className="px-4 py-1.5 rounded text-sm bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
          >
            {updateM.isPending ? "Salvando…" : "Salvar alterações"}
          </button>
        </div>
      </div>
    </div>
  );
}

// avoid unused
void CheckCircle2; void Clock;
